const normalizeLineBreaks = (value) => String(value || '').replace(/\r\n/g, '\n')

export const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

export const plainTextToHtml = (value) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''

  const escaped = escapeHtml(normalizeLineBreaks(trimmed))
  const paragraphs = escaped
    .split(/\n{2,}/)
    .map((block) => block.replace(/\n/g, '<br />').trim())
    .filter(Boolean)
    .map((block) => `<p>${block}</p>`)

  return paragraphs.length ? paragraphs.join('') : `<p>${escaped}</p>`
}

const isLikelyHtml = (value) => /<[a-z][\s\S]*>/i.test(String(value || ''))

export function markdownToHtml (markdown) {
  const safeEscapeHtml = (text) =>
    String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')

  const safeUrl = (url) => {
    const trimmed = String(url || '').trim()
    if (/^(https?:\/\/|mailto:|#)/i.test(trimmed)) {
      return safeEscapeHtml(trimmed)
    }
    return '#'
  }

  const parseInline = (text) => {
    let result = safeEscapeHtml(text)
    const markdownLinks = []

    result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, url) => {
      const token = `@@SUPERDOC_LINK_${markdownLinks.length}@@`
      markdownLinks.push(`<a href="${safeUrl(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`)
      return token
    })
    result = result.replace(/`([^`]+)`/g, '<code>$1</code>')
    result = result.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    result = result.replace(/\*(.*?)\*/g, '<em>$1</em>')
    result = result.replace(/https:\/\/pubmed\.ncbi\.nlm\.nih\.gov\/\d+\/?/gi, (url) => {
      const safePubMedUrl = safeUrl(url)
      return `<a href="${safePubMedUrl}" target="_blank" rel="noopener noreferrer">${safePubMedUrl}</a>`
    })
    result = result.replace(/@@SUPERDOC_LINK_(\d+)@@/g, (_, index) => markdownLinks[Number(index)] || '')
    return result
  }

  const lines = String(markdown || '').split(/\r?\n/)
  let html = ''
  let inCodeBlock = false
  let codeBuffer = []
  let inList = false
  let inBlockquote = false

  const closeList = () => {
    if (inList) {
      html += '</ul>\n'
      inList = false
    }
  }

  const closeBlockquote = () => {
    if (inBlockquote) {
      html += '</blockquote>\n'
      inBlockquote = false
    }
  }

  const isTableSeparator = (line) =>
    /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line)

  const parseTableRow = (line) =>
    line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((cell) => cell.trim())

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed.startsWith('```')) {
      if (!inCodeBlock) {
        closeList()
        closeBlockquote()
        inCodeBlock = true
        codeBuffer = []
      } else {
        html += `<pre><code>${safeEscapeHtml(codeBuffer.join('\n'))}</code></pre>\n`
        inCodeBlock = false
      }
      continue
    }

    if (inCodeBlock) {
      codeBuffer.push(line)
      continue
    }

    if (!trimmed) {
      closeList()
      closeBlockquote()
      continue
    }

    if (/^---+$/.test(trimmed)) {
      closeList()
      closeBlockquote()
      html += '<hr />\n'
      continue
    }

    if (trimmed.includes('|') && lines[i + 1] && isTableSeparator(lines[i + 1])) {
      closeList()
      closeBlockquote()
      const headers = parseTableRow(trimmed)
      i += 2
      const rows = []
      while (i < lines.length && lines[i].trim().includes('|')) {
        rows.push(parseTableRow(lines[i]))
        i++
      }
      i--

      html += '<table>\n<thead>\n<tr>\n'
      headers.forEach((header) => {
        html += `<th>${parseInline(header)}</th>\n`
      })
      html += '</tr>\n</thead>\n<tbody>\n'
      rows.forEach((row) => {
        html += '<tr>\n'
        row.forEach((cell) => {
          html += `<td>${parseInline(cell)}</td>\n`
        })
        html += '</tr>\n'
      })
      html += '</tbody>\n</table>\n'
      continue
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/)
    if (headingMatch) {
      closeList()
      closeBlockquote()
      const level = headingMatch[1].length
      html += `<h${level}>${parseInline(headingMatch[2])}</h${level}>\n`
      continue
    }

    if (trimmed.startsWith('>')) {
      closeList()
      if (!inBlockquote) {
        html += '<blockquote>\n'
        inBlockquote = true
      }
      html += `<p>${parseInline(trimmed.replace(/^>\s?/, ''))}</p>\n`
      continue
    }

    const listMatch = trimmed.match(/^[-*◈]\s+(.*)$/)
    if (listMatch) {
      closeBlockquote()
      if (!inList) {
        html += '<ul>\n'
        inList = true
      }
      html += `<li>${parseInline(listMatch[1])}</li>\n`
      continue
    }

    closeList()
    closeBlockquote()
    html += `<p>${parseInline(trimmed)}</p>\n`
  }

  closeList()
  closeBlockquote()

  return html.trim()
}

export const stripHtml = (html) => {
  if (!html) return ''
  if (typeof window === 'undefined' || !window.document) {
    return String(html).replace(/<[^>]*>/g, ' ')
  }
  const parser = document.createElement('div')
  parser.innerHTML = html
  return parser.textContent || parser.innerText || ''
}

export const formatSuperDocHtml = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (isLikelyHtml(raw)) return raw
  return markdownToHtml(raw) || plainTextToHtml(raw)
}

export const formatSuperDocPreview = (value) =>
  stripHtml(formatSuperDocHtml(value)).replace(/\s+/g, ' ').trim()
